import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ButtonComponent } from '../../../shared/components/custom-button/button.component';
import { TableComponent } from '../../../shared/components/custom-table/table.component';
import { AddEventManagerComponent } from '../../../shared/components/dialog/add-event-managers/add-event-manager.component';
import { AlertDialogComponent } from '../../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { CommonDialogComponent } from '../../../shared/components/dialog/commom/commom-dialog.component';
import { CustomToastComponent } from '../../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../../shared/services/http.service';

interface EventType {
    managerId: string;
    firstName: string;
    lastName: string;
    email: string;
    secondaryEmail: string;
    mobileNumber: number;
}

interface APIResponse {
    status: boolean;
    message: string;
    data: EventType | [];
}

@Component({
    selector: 'app-event-manager',
    standalone: true,
    imports: [
        CommonModule,
        TableComponent,
        ButtonComponent,
        MatDialogModule,
        AddEventManagerComponent
    ],
    templateUrl: './event-manager.component.html',
    styleUrls: ['./event-manager.component.scss'],
})
export class EventManagerComponent implements OnInit {
    @ViewChild('actionButtons') actionButtonsTemplate!: TemplateRef<any>;
    @ViewChild('addEventTypeContent') addEventTypeContent!: TemplateRef<any>;
    @ViewChild(TableComponent) tableComponent!: TableComponent;

    loading = false;
    submissionLoading = false
    data: EventType[] = [];
    editingEventType: EventType | null = null;
    columns: string[] = ['firstName', 'lastName', 'primaryEmail', 'secondaryEmail', 'mobileNumber'];
    fullData: any[] = [];
    search: string = '';
    constructor(
        private dialog: MatDialog,
        private httpService: HttpService,
        private snackBar: MatSnackBar,
        private cdr: ChangeDetectorRef,

    ) { }

    ngOnInit(): void {
        this.fetchEventTypes();
    }

    fetchEventTypes(): void {
        this.loading = true;

        this.httpService.get<APIResponse>('eventmanagers').subscribe({
            next: (response) => {
                const dataArray = Array.isArray(response.data) ? response.data : response.data ? [response.data] : [];
                this.fullData = dataArray.map(item => ({
                    managerId: item.managerId,
                    firstName: item.firstName || '-',
                    lastName: item.lastName || '-',
                    primaryEmail: item.email || '-',
                    secondaryEmail: item.secondaryEmail || '-',
                    mobileNumber: item.mobileNumber || '-',
                }));
                this.applyFilter();
            },
            error: (err) => {
                console.error('Failed to load Event Types:', err);
                this.showErrorToast(err?.message);
                this.loading = false;
            },
            complete: () => {
                this.loading = false;
            }
        });
    }


    updateSearch(searchValue: string): void {
        this.search = searchValue;
        this.applyFilter();
        this.cdr.detectChanges();
    }
    ngAfterViewInit(): void {
        this.cdr.detectChanges();
    }

    applyFilter() {
        if (!this.search || this.search.trim() === '') {
            this.data = [...this.fullData];
            return;
        }
        const search = this.search.trim().toLowerCase();
        this.data = this.fullData.filter(csqTeam =>
            csqTeam.firstName.toLowerCase().includes(search) ||
            csqTeam.lastName.toLowerCase().includes(search) ||
            csqTeam.primaryEmail.toLowerCase().includes(search) ||
            csqTeam.secondaryEmail.toLowerCase().includes(search) ||
            csqTeam.mobileNumber.toLowerCase().includes(search)
        );
    }

    onEventTypeFormSubmit(formData: any): void {
        if (this.submissionLoading) return
        this.submissionLoading = true
        const payload = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.primaryEmail,
            secondaryEmail: formData.secondaryEmail,
            mobileNumber: formData.mobileNumber,
        };

        const apiCall = this.editingEventType
            ? this.httpService.put<APIResponse>(`eventmanagers/${this.editingEventType.managerId}`, payload)
            : this.httpService.post<APIResponse>('eventmanagers', payload);


        apiCall.subscribe({
            next: (response) => {
                if (response.status) {
                    this.showSuccessToast(response.message);
                    this.dialog.closeAll();
                    this.fetchEventTypes();
                    this.editingEventType = null;
                } else {
                    this.showErrorToast(response.message);
                    return;
                }
                this.submissionLoading = false
            },
            error: (err) => {
                console.error('Failed to save Event Type:', err);
                this.showErrorToast(err?.message);
                this.submissionLoading = false
            },
            complete: () => {
                this.submissionLoading = false
            }
        });
    }

    openAddDialog(eventTypeToEdit: EventType | null = null): void {
        this.editingEventType = eventTypeToEdit;

        const dialogRef = this.dialog.open(CommonDialogComponent, {
            data: {
                title: eventTypeToEdit ? 'Edit Event Manager' : 'Add New Event Manager',
                confirmText: eventTypeToEdit ? 'Update' : 'Save',
                cancelText: 'Cancel',
                template: this.addEventTypeContent
            },
            width: '50.5rem',
            maxWidth: '100vw',
            disableClose: true,
            autoFocus: false
        });

        dialogRef.afterClosed().subscribe(() => {
            this.editingEventType = null;
        });
    }

    onEdit(eventType: EventType): void {
        this.openAddDialog(eventType);
    }

    onDelete(eventType: EventType): void {
        const dialogRef = this.dialog.open(AlertDialogComponent, {
            width: '25.438rem',
            data: {
                title: 'Confirm delete',
                message: `Are you sure you want to delete ${eventType.firstName}?`,
                okText: 'Delete',
                cancelText: 'Cancel',
                icon: 'warning',
                submessage: ""
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.deleteEventType(eventType);
            }
        });
    }

    private deleteEventType(eventType: EventType): void {
        this.httpService.delete<APIResponse>(`eventmanagers/${eventType.managerId}`).subscribe({
            next: (response) => {
                if (response.status) {
                    this.data = this.data.filter(item => item.managerId !== eventType.managerId);
                    this.adjustPaginationAfterDelete();
                    this.showSuccessToast(response.message);
                } else {
                    this.showErrorToast(response.message);
                    return;

                }
            },
            error: (err) => {
                console.error('Failed to delete Event Type:', err);
                this.showErrorToast(err?.message);
            },
        });
    }

    private adjustPaginationAfterDelete(): void {
        if (!this.tableComponent) return;

        const totalPages = Math.ceil(this.data.length / this.tableComponent.pageSize);
        if (this.tableComponent.currentPage > totalPages && totalPages > 0) {
            this.tableComponent.currentPage = totalPages;
        }
    }

    private showSuccessToast(message: string): void {
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'success-toast']
        });
    }

    private showErrorToast(message: string | null | undefined): void {
        if (!message) return;
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'error-toast']
        });
    }
}